import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Alert, AlertDescription } from './ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Upload, ShieldCheck, ShieldAlert, FileBadge, Trash2 } from 'lucide-react';
import { SicoobPixPanel } from './SicoobPixPanel';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const SettingsNFCeTab = ({ settings, onChange, onSaveCallback }) => {
  const [status, setStatus] = useState(null);
  const [pwd, setPwd] = useState('');
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  const loadStatus = async () => {
    try {
      const r = await axios.get(`${API}/nfce/certificate/status`, { withCredentials: true });
      setStatus(r.data);
    } catch (e) { /* ignore */ }
  };

  useEffect(() => { loadStatus(); }, []);

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!file) { setErr('Selecione o arquivo .pfx'); return; }
    if (!pwd) { setErr('Informe a senha do certificado'); return; }
    setBusy(true); setErr(''); setMsg('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('password', pwd);
      const r = await axios.post(`${API}/nfce/certificate/upload`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setMsg(`Certificado importado: ${r.data.subject || 'OK'}`);
      setFile(null); setPwd('');
      loadStatus();
      if (onSaveCallback) onSaveCallback();
    } catch (e) {
      setErr(e.response?.data?.detail || 'Erro ao enviar certificado');
    } finally { setBusy(false); }
  };

  const handleDelete = async () => {
    if (!window.confirm('Remover o certificado digital atual?')) return;
    try {
      await axios.delete(`${API}/nfce/certificate`, { withCredentials: true });
      setMsg('Certificado removido');
      loadStatus();
    } catch (e) {
      setErr(e.response?.data?.detail || 'Erro ao remover');
    }
  };

  const expired = status?.expires_at && new Date(status.expires_at) < new Date();
  const expiresSoon = status?.expires_at && new Date(status.expires_at) < new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);

  return (
    <div className="space-y-6" data-testid="nfce-settings">
      <div className="bg-blue-50 border border-blue-200 rounded p-3 text-sm text-blue-900">
        <p className="font-bold flex items-center gap-2"><ShieldAlert size={14} /> Transmissão via Focus NFe</p>
        <p className="text-xs mt-1">Com o token da Focus NFe configurado acima e a empresa sincronizada, a NFC-e é transmitida à SEFAZ-MS e autorizada em tempo real. Sem token, o sistema gera apenas um rascunho local (sem validade fiscal). Se a SEFAZ estiver fora, a NFC-e sai em contingência offline e é retransmitida automaticamente.</p>
      </div>

      {/* Habilitar NFCe + configs SEFAZ */}
      <div className="bg-white border border-slate-200 rounded-md p-4">
        <h3 className="font-bold text-slate-900 mb-3 flex items-center gap-2"><FileBadge size={16} /> Parâmetros NFCe</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="flex items-center gap-3 md:col-span-2">
            <input
              id="nfce_enabled" type="checkbox"
              checked={!!settings.nfce_enabled}
              onChange={(e) => onChange({ ...settings, nfce_enabled: e.target.checked })}
              data-testid="nfce-enabled"
            />
            <Label htmlFor="nfce_enabled" className="cursor-pointer">Habilitar emissão de NFCe (rascunho)</Label>
          </div>
          <div>
            <Label>Ambiente</Label>
            <Select value={settings.nfce_ambiente || 'homologacao'} onValueChange={(v) => onChange({ ...settings, nfce_ambiente: v })}>
              <SelectTrigger data-testid="nfce-ambiente"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="homologacao">Homologação (testes)</SelectItem>
                <SelectItem value="producao">Produção</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Série</Label>
            <Input value={settings.nfce_serie || '1'} onChange={(e) => onChange({ ...settings, nfce_serie: e.target.value })} placeholder="1" data-testid="nfce-serie" />
          </div>
          <div>
            <Label>Próximo número</Label>
            <Input type="number" value={settings.nfce_next_number || 1} onChange={(e) => onChange({ ...settings, nfce_next_number: parseInt(e.target.value || '1', 10) })} placeholder="1" data-testid="nfce-next-number" />
          </div>
          <div>
            <Label>CSC ID</Label>
            <Input value={settings.nfce_csc_id || ''} onChange={(e) => onChange({ ...settings, nfce_csc_id: e.target.value })} placeholder="1" data-testid="nfce-csc-id" />
          </div>
          <div className="md:col-span-2">
            <Label>CSC (Código de Segurança do Contribuinte)</Label>
            <Input value={settings.nfce_csc || ''} onChange={(e) => onChange({ ...settings, nfce_csc: e.target.value })} placeholder="Obtenha no Portal SEFAZ" data-testid="nfce-csc" />
          </div>
        </div>
      </div>

      {/* Certificado Digital A1 */}
      <div className="bg-white border border-slate-200 rounded-md p-4">
        <h3 className="font-bold text-slate-900 mb-3 flex items-center gap-2"><ShieldCheck size={16} /> Certificado Digital A1 (.pfx)</h3>

        {status?.present ? (
          <div className={`rounded p-3 mb-3 ${expired ? 'bg-red-50 border border-red-200' : expiresSoon ? 'bg-amber-50 border border-amber-200' : 'bg-emerald-50 border border-emerald-200'}`} data-testid="cert-status-card">
            <p className="font-bold text-slate-900">{status.subject || 'Certificado instalado'}</p>
            <p className="text-xs text-slate-600">Arquivo: {status.filename}</p>
            <p className={`text-xs font-bold ${expired ? 'text-red-700' : expiresSoon ? 'text-amber-700' : 'text-emerald-700'}`}>
              {expired ? '⚠ EXPIRADO em' : expiresSoon ? '⚠ Expira em breve:' : '✓ Válido até:'} {status.expires_at ? new Date(status.expires_at).toLocaleDateString('pt-BR') : '—'}
            </p>
            <Button onClick={handleDelete} variant="outline" size="sm" className="mt-2 text-red-600 border-red-300 hover:bg-red-50" data-testid="cert-delete">
              <Trash2 size={14} className="mr-1" /> Remover Certificado
            </Button>
          </div>
        ) : (
          <p className="text-sm text-slate-500 mb-3">Nenhum certificado instalado ainda.</p>
        )}

        <form onSubmit={handleUpload} className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="md:col-span-2">
            <Label>Arquivo .pfx / .p12</Label>
            <Input type="file" accept=".pfx,.p12" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="cert-file" />
          </div>
          <div className="md:col-span-2">
            <Label>Senha do Certificado</Label>
            <Input type="password" value={pwd} onChange={(e) => setPwd(e.target.value)} placeholder="senha do .pfx" data-testid="cert-password" />
          </div>
          {err && <div className="md:col-span-2"><Alert variant="destructive"><AlertDescription>{err}</AlertDescription></Alert></div>}
          {msg && <div className="md:col-span-2"><Alert className="border-emerald-300 bg-emerald-50"><AlertDescription className="text-emerald-700">{msg}</AlertDescription></Alert></div>}
          <div className="md:col-span-2">
            <Button type="submit" disabled={busy || !file} className="bg-blue-600 hover:bg-blue-700" data-testid="cert-upload">
              <Upload size={16} className="mr-2" /> {busy ? 'Enviando...' : 'Enviar Certificado'}
            </Button>
          </div>
        </form>

        <p className="text-xs text-slate-500 mt-3">
          O arquivo .pfx fica armazenado no servidor de forma segura. A senha é necessária para assinar os XMLs de NFCe.
        </p>
      </div>
    </div>
  );
};

export const SettingsPixTab = ({ settings, onChange }) => {
  return (
    <div className="space-y-4" data-testid="pix-settings">
      <div className="bg-emerald-50 border border-emerald-200 rounded p-3 text-sm text-emerald-900">
        <p className="font-bold">PIX QR Code Estático — sem custo</p>
        <p className="text-xs mt-1">
          Cadastre sua chave PIX abaixo. O PDV gera um QR Code "Copia e Cola" com o valor exato da venda. O cliente paga pelo banco
          dele e você confere o recebimento manualmente no seu app bancário.
        </p>
      </div>
      <div className="bg-white border border-slate-200 rounded-md p-4 grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <Label>Tipo de chave PIX</Label>
          <Select value={settings.pix_key_type || 'cpf'} onValueChange={(v) => onChange({ ...settings, pix_key_type: v })}>
            <SelectTrigger data-testid="pix-key-type"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="cpf">CPF</SelectItem>
              <SelectItem value="cnpj">CNPJ</SelectItem>
              <SelectItem value="email">E-mail</SelectItem>
              <SelectItem value="telefone">Telefone</SelectItem>
              <SelectItem value="aleatoria">Chave Aleatória</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Chave PIX *</Label>
          <Input
            value={settings.pix_key || ''}
            onChange={(e) => onChange({ ...settings, pix_key: e.target.value })}
            placeholder="Sua chave PIX"
            data-testid="pix-key"
          />
        </div>
        <div>
          <Label>Nome do titular (até 25 caract.)</Label>
          <Input
            value={settings.pix_merchant_name || ''}
            onChange={(e) => onChange({ ...settings, pix_merchant_name: e.target.value })}
            maxLength={25}
            placeholder="MARGE HORTIFRUTI LTDA"
            data-testid="pix-merchant-name"
          />
        </div>
        <div>
          <Label>Cidade (até 15 caract.)</Label>
          <Input
            value={settings.pix_merchant_city || ''}
            onChange={(e) => onChange({ ...settings, pix_merchant_city: e.target.value })}
            maxLength={15}
            placeholder="CAARAPO"
            data-testid="pix-merchant-city"
          />
        </div>
      </div>
      <SicoobPixPanel pixKey={settings.pix_key} />
    </div>
  );
};
